// The date-window preset vocabulary, as data.
//
// Split out of DateWindow.js so that consumers of the DATA are not consumers of the WIDGET.
// DateWindow imports react, styled-components, react-date-picker and two vendor stylesheets; the
// feed viewer's knowableWindow.js — and transitively its rowFilters.js predicates, which have
// nothing to do with dates being picked — sat downstream of all of it for the sake of one array.
// Swapping the date picker should not touch the pure modules, and a jest run of a pure suite should
// not parse a calendar stylesheet.
//
// Array ORDER is load-bearing: the chips render in it.

// A preset is one of five shapes:
//   { amount, unit }   a trailing duration, e.g. 3 months back from the upper bound
//   { key: 'ytd' }     January 1st of the upper bound's year
//   { key: 'latest' }  the upper bound's own day — a one-day window, not a trailing span
//   { key: 'max' }     the earliest allowed date — or, with `clears`, no bounds at all
//   { key: 'custom' }  reveals the two date pickers and changes nothing
//
// `clears` matters where an unset window is meaningful on its own. The feed viewer's knowable
// window is optional and unset means the whole history, so its Max clears the field rather than
// pinning an arbitrary floor the caller never asked for.
//
// `latest` cannot be spelled as a trailing duration: `{ amount: 0, unit: 'day' }` has a falsy
// amount, so it would fall through the amount branch and resolve to the maxWindowYears floor — the
// widest window on offer where the narrowest was meant.

// The occasion window on a backfill, which is bounded by maxWindowYears and always has a value.
export const OCCASION_WINDOW_PRESETS = [
  // Shortest first, and the only sub-year span here: a backfill spends real money per occasion, so
  // the cheapest useful window is the one that should be one press away rather than a custom date.
  { key: '3m', label: 'Last 3m', amount: 3, unit: 'month' },
  { key: '1y', label: 'Last 1y', amount: 1, unit: 'year' },
  { key: '3y', label: 'Last 3y', amount: 3, unit: 'year' },
  { key: '5y', label: 'Last 5y', amount: 5, unit: 'year' },
  { key: 'ytd', label: 'YTD' },
  { key: 'max', label: 'Max' },
];

// The knowable window on a feed view. Short trailing spans, because a feed is usually read for
// what has landed recently; Max is genuinely unbounded; Custom opens the pickers.
export const KNOWABLE_WINDOW_PRESETS = [
  { key: '1w', label: '1w', amount: 1, unit: 'week' },
  { key: '1m', label: '1m', amount: 1, unit: 'month' },
  { key: '3m', label: '3m', amount: 3, unit: 'month' },
  { key: '6m', label: '6m', amount: 6, unit: 'month' },
  { key: '1y', label: '1y', amount: 1, unit: 'year' },
  { key: 'max', label: 'Max', clears: true },
  { key: 'custom', label: 'Custom' },
];

// Narrowing rows that are ALREADY loaded, on the feed viewer's results table. Every span here is
// resolved against the newest loaded row rather than against today — the caller passes the loaded
// maximum as `maxDate`, which is what `to` falls back from — because a trailing window counted from
// now selects nothing on a feed whose last occasion was six weeks ago.
//
// The caller is expected to drop presets wider than the rows in hand (see
// feedView/knowableWindow.js): offering one that can only reproduce the unfiltered table wastes a
// chip, and two presets that both clamp to the loaded floor would read as pressed at once.
export const LOADED_WINDOW_PRESETS = [
  { key: 'latest', label: 'Latest' },
  { key: '7d', label: '7d', amount: 7, unit: 'day' },
  { key: '30d', label: '30d', amount: 30, unit: 'day' },
  { key: '90d', label: '90d', amount: 90, unit: 'day' },
  { key: 'ytd', label: 'YTD' },
  { key: 'all', label: 'All', clears: true },
  { key: 'custom', label: 'Custom' },
];
